import {
    AfterViewChecked,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { CalendarModule } from 'primeng/calendar';
import { Dropdown, DropdownModule } from 'primeng/dropdown';
import { SelectButtonModule } from 'primeng/selectbutton';

export type InlineEditType = 'text' | 'textarea' | 'number' | 'date' | 'select' | 'selectButton';

/**
 * Click-to-edit field: display until clicked, then input with autofocus; saves on blur/change.
 */
@Component({
    selector: 'app-inline-editable-field',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        InputTextModule,
        InputTextareaModule,
        CalendarModule,
        DropdownModule,
        SelectButtonModule
    ],
    template: `
        <div class="inline-edit flex align-items-center gap-2 w-full"
             [class.inline-edit--pulse]="pulsing"
             [class.inline-edit--readonly]="readonly">
            @if (!editing) {
                <span class="inline-edit__display champConsultation"
                      [class.cursor-pointer]="!readonly"
                      [class.inline-edit__display--editable]="!readonly"
                      (click)="startEdit()"
                      tabindex="0"
                      (keydown.enter)="startEdit()">
                    {{ displayValue || '—' }}
                </span>
            } @else {
                @if (editorTpl) {
                    <ng-container
                        [ngTemplateOutlet]="editorTpl"
                        [ngTemplateOutletContext]="editorContext">
                    </ng-container>
                } @else if (type === 'textarea') {
                    <textarea #inputEl
                              pInputTextarea
                              class="w-full"
                              [(ngModel)]="draft"
                              (blur)="commit()"
                              (keydown.escape)="cancel()"
                              rows="3"></textarea>
                } @else if (type === 'date') {
                    <p-calendar #calendarEl
                                [(ngModel)]="draft"
                                dateFormat="yy-mm-dd"
                                [showIcon]="true"
                                appendTo="body"
                                (onSelect)="commit()"
                                (onClose)="commit()"></p-calendar>
                } @else if (type === 'select') {
                    <p-dropdown #dropdownEl
                                class="w-full"
                                [options]="options"
                                [(ngModel)]="draft"
                                [optionLabel]="optionLabel"
                                [dataKey]="dataKey"
                                [filter]="filter"
                                [filterBy]="filterBy"
                                [showClear]="showClear"
                                [placeholder]="placeholder"
                                [virtualScroll]="virtualScroll"
                                [virtualScrollItemSize]="virtualScrollItemSize"
                                appendTo="body"
                                (onChange)="commit()"
                                (onHide)="onSelectHide()">
                        <ng-content></ng-content>
                    </p-dropdown>
                } @else if (type === 'selectButton') {
                    <p-selectButton
                        [options]="options"
                        [(ngModel)]="draft"
                        [optionLabel]="optionLabel"
                        [optionValue]="optionValue"
                        [allowEmpty]="false"
                        (onChange)="commit()">
                    </p-selectButton>
                } @else {
                    <input #inputEl
                           pInputText
                           class="w-full"
                           [type]="type === 'number' ? 'number' : 'text'"
                           [(ngModel)]="draft"
                           (blur)="commit()"
                           (keydown.enter)="commit()"
                           (keydown.escape)="cancel()" />
                }
            }
            @if (showSaved) {
                <i class="pi pi-check text-green-500 flex-shrink-0" aria-label="Enregistré"></i>
            }
            @if (saving) {
                <i class="pi pi-spin pi-spinner flex-shrink-0" aria-label="Enregistrement"></i>
            }
        </div>
    `,
    styles: [`
        :host { display: block; width: 100%; }
        .inline-edit__display--editable:hover,
        :host ::ng-deep .inline-edit__display--editable:hover {
            text-decoration: underline;
            text-underline-offset: 2px;
        }
        .inline-edit--pulse {
            animation: inline-pulse 0.8s ease;
            border-radius: 0.25rem;
        }
        @keyframes inline-pulse {
            0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--primary-color) 45%, transparent); }
            70% { box-shadow: 0 0 0 8px transparent; }
            100% { box-shadow: 0 0 0 0 transparent; }
        }
    `]
})
export class InlineEditableFieldComponent implements OnChanges, AfterViewChecked {
    @Input() value: any;
    @Input() type: InlineEditType = 'text';
    @Input() readonly = false;
    @Input() displayFn?: (value: any) => string;
    @Input() pulseToken: number | string | null = null;
    @Input() options: any[] = [];
    @Input() optionLabel = 'label';
    @Input() optionValue: string | null = null;
    @Input() dataKey: string | null = null;
    @Input() filter = false;
    @Input() filterBy: string = '';
    @Input() showClear = false;
    @Input() placeholder = '';
    @Input() virtualScroll = false;
    @Input() virtualScrollItemSize = 38;

    @Output() save = new EventEmitter<any>();

    @ContentChild('editor') editorTpl?: TemplateRef<{ commit: (v?: any) => void; cancel: () => void }>;

    @ViewChild('inputEl') inputEl?: ElementRef<HTMLElement>;
    @ViewChild('dropdownEl') dropdownEl?: Dropdown;
    @ViewChild('calendarEl') calendarEl?: any;

    editing = false;
    draft: any;
    saving = false;
    showSaved = false;
    pulsing = false;
    private lastPulseToken: number | string | null = null;
    private savedTimer: any;
    private needsFocus = false;
    /** Value emitted on the last save; used to clear spinner when parent applies it. */
    private pendingCommit: any = undefined;

    get editorContext(): { commit: (v?: any) => void; cancel: () => void } {
        return {
            commit: (v?: any) => this.commit(v),
            cancel: () => this.cancel()
        };
    }

    get displayValue(): string {
        if (this.displayFn) {
            return this.displayFn(this.value) ?? '';
        }
        if (this.value instanceof Date) {
            return this.value.toLocaleDateString();
        }
        if (this.type === 'select' && this.value && this.optionLabel) {
            return this.value[this.optionLabel] ?? String(this.value);
        }
        if (this.type === 'selectButton' && this.options?.length) {
            const match = this.options.find(o =>
                (this.optionValue ? o[this.optionValue] : o) === this.value
            );
            return match ? (match[this.optionLabel] ?? String(this.value)) : String(this.value ?? '');
        }
        return this.value == null || this.value === '' ? '' : String(this.value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pulseToken'] && this.pulseToken != null && this.pulseToken !== this.lastPulseToken) {
            this.lastPulseToken = this.pulseToken;
            this.triggerPulse();
        }
        if (changes['value'] && !this.editing) {
            this.draft = this.value;
        }
        // Clear spinner once parent reflects the committed value (or any value refresh while saving).
        if (this.saving && !this.editing) {
            const valueCaughtUp = this.pendingCommit !== undefined && this.valuesEqual(this.value, this.pendingCommit);
            if (changes['value'] || valueCaughtUp) {
                this.markSaved(true);
            }
        }
    }

    ngAfterViewChecked(): void {
        if (!this.needsFocus || !this.editing) {
            return;
        }
        this.needsFocus = false;
        setTimeout(() => this.focusEditor(), 0);
    }

    startEdit(): void {
        if (this.readonly || this.editing) {
            return;
        }
        this.draft = this.value instanceof Date ? new Date(this.value) : this.value;
        this.editing = true;
        this.needsFocus = true;
    }

    commit(next?: any): void {
        if (!this.editing) {
            return;
        }
        if (next !== undefined) {
            this.draft = next;
        }
        const valeur = this.draft;
        const prev = this.value;
        const same = this.valuesEqual(valeur, prev);
        this.editing = false;
        if (same) {
            return;
        }
        this.pendingCommit = valeur;
        this.saving = true;
        this.save.emit(valeur);
    }

    onSelectHide(): void {
        if (this.editing) {
            this.editing = false;
            this.draft = this.value;
        }
    }

    /** Called by parent after save completes (or automatically when [value] catches up). */
    markSaved(success: boolean): void {
        this.saving = false;
        this.pendingCommit = undefined;
        if (!success) {
            this.draft = this.value;
            return;
        }
        this.showSaved = true;
        clearTimeout(this.savedTimer);
        this.savedTimer = setTimeout(() => this.showSaved = false, 2000);
    }

    cancel(): void {
        this.editing = false;
        this.draft = this.value;
    }

    triggerPulse(): void {
        this.pulsing = false;
        requestAnimationFrame(() => {
            this.pulsing = true;
            setTimeout(() => this.pulsing = false, 800);
        });
    }

    private focusEditor(): void {
        if (this.inputEl?.nativeElement) {
            this.inputEl.nativeElement.focus();
            if (this.inputEl.nativeElement instanceof HTMLInputElement
                || this.inputEl.nativeElement instanceof HTMLTextAreaElement) {
                this.inputEl.nativeElement.select?.();
            }
            return;
        }
        if (this.dropdownEl) {
            this.dropdownEl.show();
            const focusable = (this.dropdownEl.el?.nativeElement as HTMLElement)
                ?.querySelector?.('input, .p-dropdown') as HTMLElement;
            focusable?.focus?.();
            return;
        }
        if (this.calendarEl) {
            this.calendarEl.inputfieldViewChild?.nativeElement?.focus?.();
            this.calendarEl.showOverlay?.();
        }
    }

    private valuesEqual(a: any, b: any): boolean {
        if (a instanceof Date && b instanceof Date) {
            return a.getTime() === b.getTime();
        }
        if (this.dataKey && a && b) {
            return a[this.dataKey] === b[this.dataKey];
        }
        return a === b;
    }
}
